import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { AppSidebar } from "@/components/AppSidebar";
import { MobileTopBar } from "@/components/MobileTopBar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { UtilisationTablesPanel } from "@/components/UtilisationTablesPanel";
import { MultiSelectFilter } from "@/components/UtilisationFilterBar";
import { fetchFilterOptions } from "@/analytics/api";
import type { UtilisationFilters } from "@/analytics/types";
import {
  downloadUtilisationDrillThroughExcel,
  type UtilisationExportOption,
} from "@/analytics/utilisationReports";
import {
  DrillThroughExportControl,
  type DrillThroughExportOption,
} from "@/components/DrillThroughExportControl";
import { useToast } from "@/hooks/use-toast";

type UtilisationTablesLocationState = {
  filters?: UtilisationFilters;
} | null;

const emptyFilters: UtilisationFilters = {
  legacy_location: [],
  employees: [],
  emp_categories: [],
  service_lines: [],
  date_from: null,
  date_to: null,
};

const EXPORT_OPTIONS: readonly DrillThroughExportOption<UtilisationExportOption>[] = [
  { value: "kpi_by_office", label: "KPI by Office" },
  { value: "employee_month_utilisation", label: "Employee × Month Utilisation" },
  { value: "employee_month_hours", label: "Employee × Month Hours" },
];

export default function UtilisationTablesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { state } = useLocation();
  const dashboardFilters = (state as UtilisationTablesLocationState)?.filters;

  const filters = useMemo(() => dashboardFilters, [dashboardFilters]);

  const [serviceLines, setServiceLines] = useState<string[]>(() => dashboardFilters?.service_lines ?? []);
  const [serviceLineOptions, setServiceLineOptions] = useState<string[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    fetchFilterOptions(undefined, controller.signal)
      .then((options) => setServiceLineOptions(options.service_lines))
      .catch(() => {});
    return () => controller.abort();
  }, []);

  const toggleServiceLine = (value: string) => {
    setServiceLines((current) =>
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
    );
  };

  const effectiveFilters = useMemo<UtilisationFilters | undefined>(
    () => (filters ? { ...filters, service_lines: serviceLines } : undefined),
    [filters, serviceLines],
  );

  const [isExporting, setIsExporting] = useState(false);
  const [exportOptions, setExportOptions] = useState<UtilisationExportOption[]>([]);

  const toggleExportOption = (option: UtilisationExportOption) => {
    setExportOptions((current) => current.includes(option)
      ? current.filter((value) => value !== option)
      : [...current, option]);
  };

  const handleExport = async () => {
    if (isExporting || exportOptions.length === 0 || !effectiveFilters) return;

    setIsExporting(true);
    try {
      await downloadUtilisationDrillThroughExcel(effectiveFilters, exportOptions);
    } catch (caught) {
      toast({
        variant: "destructive",
        title: "Couldn’t export Utilisation drill-through",
        description: caught instanceof Error ? caught.message : "Failed to export Excel file",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-dvh flex w-full bg-background">
        <AppSidebar />

        <div className="flex flex-1 flex-col min-w-0 h-dvh overflow-hidden">
          <MobileTopBar title="Utilisation Tables" />
          <main className="flex-1 overflow-auto p-2 sm:p-3 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3 shadow-sm">
              <div>
                <h1 className="text-lg font-semibold text-foreground">Utilisation Tables</h1>
                <p className="text-xs text-muted-foreground">Using the filters selected on the Utilisation dashboard.</p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <MultiSelectFilter
                  label="Service Line"
                  options={[...new Set([...serviceLineOptions, ...serviceLines])]}
                  availableOptions={serviceLineOptions}
                  selected={serviceLines}
                  onToggle={toggleServiceLine}
                />
                <DrillThroughExportControl
                  options={EXPORT_OPTIONS}
                  selectedOptions={exportOptions}
                  onToggleOption={toggleExportOption}
                  onDownload={handleExport}
                  isExporting={isExporting}
                />
                <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate(-1)}>
                  <ArrowLeft className="h-4 w-4" />
                  Back to Utilisation
                </Button>
              </div>
            </div>

            {effectiveFilters && <UtilisationTablesPanel filters={effectiveFilters} />}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
